// navigation.service.ts
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, tap, of, lastValueFrom } from 'rxjs';

export interface RouteLevel {
  route: string;
  label: string;
  level: number;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  public _translate = inject(TranslateService);
  private routeHierarchy: { [key: string]: RouteLevel } = {
    '': { route: '', label: 'NAVIGATION.MAIN_PAGE', level: 0 },
    'message': { route: 'message', label: 'NAVIGATION.MESSAGE_PAGE', level: 1 },
    'message/detail': { route: 'message/detail', label: 'NAVIGATION.MESSAGE_DETAIL_PAGE', level: 2 },
    'chart': { route: 'chart', label: 'NAVIGATION.CHART_PAGE', level: 1 },
    'chart/detail': { route: 'chart/detail', label: 'NAVIGATION.CHART_DETAIL_PAGE', level: 2 },
    'table': { route: 'table', label: 'NAVIGATION.TABLE_PAGE', level: 1 },
    'table/detail': { route: 'table/detail', label: 'NAVIGATION.TABLE_DETAIL_PAGE', level: 2 },
    'transaction': { route: 'transaction', label: 'NAVIGATION.TRANSACTION_PAGE', level: 1 },
    'transaction/detail': { route: 'transaction/detail', label: 'NAVIGATION.TRANSACTION_DETAIL_PAGE', level: 2 },
    'settings': { route: 'settings', label: 'NAVIGATION.SETTINGS_PAGE', level: 1 },
    'test': { route: 'test', label: 'NAVIGATION.TEST_PAGE', level: 1 }
  };

  constructor(private router: Router) {
    this.initializeMauiBridge();
  }
  private analyzeUrl(url: string): { segments: string[]; routeKey: string; parentRoute: string | null } {
    const clean = url.split(/[?#]/)[0].replace(/^\/+|\/+$/g, '');
    const segments = clean ? clean.split('/') : [];

    const routeKey = segments.length === 0 ? '' :
      (segments[1] === 'detail' ? `${segments[0]}/detail` : segments[0]);
    let parentRoute: string | null;
    if (segments.length === 0) {
      parentRoute = null;
    } else if (segments.length === 1) {
      parentRoute = '';
    } else if (segments[1] === 'detail') {
      parentRoute = segments[0];
    } else {
      const currentLevel = this.routeHierarchy[routeKey];
      if (!currentLevel || currentLevel.level === 0) parentRoute = null;
      else if (currentLevel.level === 1) parentRoute = '';
      else parentRoute = null; // This can be extended to lower routes
    }

    return { segments, routeKey, parentRoute };
  }

  getParentRoute(): string | null {
    const { parentRoute } = this.analyzeUrl(this.router.url);
    return parentRoute;
  }

  canNavigateUp(): boolean {
    return this.getParentRoute() !== null;
  }

  navigateUp(): void {
    const parentRoute = this.getParentRoute();
    if (parentRoute !== null) {
      const target = parentRoute === '' ? '/' : `/${parentRoute}`;
      this.router.navigateByUrl(target);
    } else {
      console.log('[NavigationService] Cannot navigate up - no parent route');
    }
  }

  getBackButtonLabel(): string {
    const parentRoute = this.getParentRoute();
    if (!parentRoute && parentRoute !== '') return '';

    const parentLevel = this.routeHierarchy[parentRoute];
    return parentLevel ? `Back to ${parentLevel.label}` : 'Back';
  }

  getTranslatedBackText(): string {
    const parentRoute = this.getParentRoute();
    if (parentRoute === null) return '';

    const parentInfo = this.getRouteInfo(parentRoute);

    if (parentInfo) {
      const backToText = this._translate.instant('NAVIGATION.BACK_TO');
      const pageName = this._translate.instant(parentInfo.label);
      return `${backToText} ${pageName}`;
    }

    return '';
  }

  public shouldShowExitConfirmation(): boolean {
    return this.isOnRootPage();
  }


  getCurrentPageInfo(): RouteLevel | null {
    const currentUrl = this.router.url.split('?')[0];
    const currentRoute = this.extractRouteKey(currentUrl);
    return this.routeHierarchy[currentRoute] || null;
  }

  getRouteInfo(routeKey: string): RouteLevel | null {
    return this.routeHierarchy[routeKey] || null;
  }

  private extractRouteKey(url: string): string {
    return this.analyzeUrl(url).routeKey;
  }

  public handleBackButton(): boolean {
    const windowWithCallback = window as any;
    if (windowWithCallback.parentFrameBack && typeof windowWithCallback.parentFrameBack === 'function') {
      windowWithCallback.parentFrameBack();
    }

    if (!this.canNavigateUp()) {
      console.log('[NavigationService] Cannot navigate up - returning false');
      return false;
    }

    this.navigateUp();
    return true;
  }

  public isOnRootPage(): boolean {
    const currentPageInfo = this.getCurrentPageInfo();
    const isRoot = currentPageInfo?.level === 0;
    console.log('[NavigationService] isOnRootPage check:', {
      currentUrl: this.router.url,
      currentPageInfo,
      isRoot
    });
    return isRoot;
  }

  public handleFallbackNavigation(): boolean {
    if (window.history && window.history.length > 1) {
      window.history.back();
      return true;
    }

    console.log('[NavigationService] No fallback navigation available');
    return false;
  }

  public getNavigationState(): any {
    const state = {
      error: null,
      canNavigateUp: this.canNavigateUp(),
      currentRoute: this.router.url,
      backButtonLabel: this.getTranslatedBackText(),
      currentPageInfo: this.getCurrentPageInfo(),
      bridgeAvailable: !!(window as any).angularNavigation,
      timestamp: new Date().toISOString()
    };

    return state;
  }

  public initializeMauiBridge(): void {
    (window as any).angularNavigation = {
      handleBackButtonFromMaui: () => {
        if (!this.canNavigateUp()) return false;
        this.navigateUp();
        return true;
      },

      handleBackButtonFromWeb: () => {
        const cb = (window as any).parentFrameBack;
        if (!this.canNavigateUp()) return false;
        this.navigateUp();
        if (typeof cb === 'function') cb();
        return true;
      },
      handleFallbackNavigation: () => {
        return this.handleFallbackNavigation();
      },
      getNavigationState: () => {
        return this.getNavigationState();
      },
      canNavigateUp: () => {
        const canNavigate = this.canNavigateUp();
        return canNavigate;
      },
      navigateUp: () => {
        this.navigateUp();
        return true;
      },
      getCurrentRoute: () => {
        const route = this.router.url;
        return route;
      },
      getCurrentPageInfo: () => {
        const pageInfo = this.getCurrentPageInfo();
        return pageInfo;
      },
      getBackButtonLabel: () => {
        const label = this.getTranslatedBackText();
        return label;
      },
      isOnRootPage: () => {
        return this.isOnRootPage();
      }
    };
  }

  private async notifyMauiNavigationStateChange(): Promise<void> {
    const notification$ = of(null).pipe(
      tap(() => {
        if ((window as any).mauiNavigationStateChanged) {
          const state = {
            canNavigateUp: this.canNavigateUp(),
            currentRoute: this.router.url,
            backButtonLabel: this.getTranslatedBackText(),
            currentPageInfo: this.getCurrentPageInfo(),
          };
          (window as any).mauiNavigationStateChanged(state);
        }
      }),
      catchError(err => {
        console.error('[NavigationService] Error notifying MAUI:', err);
        return of(null);
      })
    );

    await lastValueFrom(notification$);
  }

  public updateMauiNavigationState(): void {
    void this.notifyMauiNavigationStateChange();
  }
}